import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { INTERNAL_ONLY_CONTACT_ROLES } from "encamp-shared/src/tierii/contacts";
import { ContactReportRole } from "generated-graphql/graphql";

import { getEPCRAContactRoles, getRcraContactRoles } from "../util/facilityContacts";
import { useFeatureFlags } from "./useFeatureFlags";

type UseEpcraContactRolesOptions = {
  /**
   * States to get EPCRA contact roles for
   */
  states?: Array<string | null | undefined>;
  /**
   * Whether to exclude internal-only roles (e.g., Regional Environmental Contact).
   * These roles are for internal tracking and should not appear on reports.
   * @default false
   */
  excludeInternalRoles?: boolean;
};

/**
 * Get the EPCRA facility contact role options for the state these facilities reside in
 */
export function useEpcraContactRoles(
  stateOrOptions?: string | null | UseEpcraContactRolesOptions,
  ...additionalStates: Array<string | null | undefined>
): {
  roles: ContactReportRole[];
} {
  // Handle both old API (variadic states) and new API (options object)
  let states: Array<string | null | undefined>;
  let excludeInternalRoles = false;

  if (typeof stateOrOptions === "object" && stateOrOptions !== null) {
    states = stateOrOptions.states ?? [];
    excludeInternalRoles = stateOrOptions.excludeInternalRoles ?? false;
  } else {
    states = [stateOrOptions, ...additionalStates];
  }

  let roles = getEPCRAContactRoles(...states);

  if (excludeInternalRoles) {
    roles = roles.filter((role) => !INTERNAL_ONLY_CONTACT_ROLES.includes(role));
  }

  return { roles };
}

export function useRcraContactRoles(...states: Array<string | null | undefined>): {
  roles: ContactReportRole[];
} {
  const { featureFlags } = useFeatureFlags();
  if (!featureFlags[FeatureFlagKey.RcraFacilityProfile]) {
    return { roles: [] };
  }
  const roles = getRcraContactRoles(...states);
  return { roles };
}
